import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiEdit,
  FiMoreVertical,
  FiChevronUp,
  FiChevronDown,
  FiRefreshCw,
  FiArchive,
  FiCopy,
} from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { BASE_URL } from "../../utils/constants";

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEditComponent = FiEdit as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMoreVerticalComponent =
  FiMoreVertical as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;
const FiRefreshCwComponent = FiRefreshCw as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiArchiveComponent = FiArchive as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCopyComponent = FiCopy as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface Automation {
  id: string;
  name: string;
  trigger: string;
  status: string;
  createdAt: string;
  createdBy: string;
  updatedOn: string;
  action: string;
  rawData?: any;
}

interface AutomationsTableProps {
  automations: Automation[];
  onRefresh?: () => void;
  isLoading?: boolean;
}

// Portal Popup Component
interface PortalPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onArchive: () => void;
  onClone: () => void;
  automation: Automation;
  anchorEl: HTMLElement | null;
}

const PortalPopup: React.FC<PortalPopupProps> = ({
  isOpen,
  onClose,
  onArchive,
  onClone,
  automation,
  anchorEl,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX - 100,
      });
    }
  }, [isOpen, anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !anchorEl) return null;

  const isArchived = automation.status?.toLowerCase() === 'archived';

  return (
    <div
      ref={popupRef}
      className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[160px] py-1"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="py-1">
        {/* Clone option - always visible */}
        <button
          onClick={() => {
            onClone();
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
        >
          <FiCopyComponent size={16} />
          Clone
        </button>

        {/* Archive option - only show if not archived */}
        {!isArchived && (
          <button
            onClick={() => {
              onArchive();
              onClose();
            }}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <FiArchiveComponent size={16} />
            Archive
          </button>
        )}
      </div>
    </div>
  );
};

// Returns the CSS classes for status badges based on the automation status
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'published':
      return 'bg-green-100 text-green-700';
    case 'draft':
      return 'bg-red-100 text-red-600';
    case 'paused':
      return 'bg-yellow-100 text-yellow-600';
    case 'archived':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const AutomationsTable: React.FC<AutomationsTableProps> = ({
  automations,
  onRefresh,
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'All' | 'Active' | 'Draft' | 'Paused'>('All');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Automation;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  // State for portal popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState<Automation | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const itemsPerPage = 6;

  // Filters automations based on the selected tab
  const filteredAutomations =
    activeTab === 'All'
      ? automations
      : automations.filter((a) => {
        if (activeTab === 'Active') return a.status?.toLowerCase() === 'active' || a.status?.toLowerCase() === 'published';
        if (activeTab === 'Draft') return a.status?.toLowerCase() === 'draft';
        if (activeTab === 'Paused') return a.status?.toLowerCase() === 'paused';
        return true;
      });

  // Sort automations with latest first (by createdAt or updatedOn)
  const sortedAutomations = React.useMemo(() => {
    // First, apply any user sorting if configured
    let sorted = [...filteredAutomations];

    if (sortConfig) {
      sorted = sorted.sort((a, b) => {
        const valA = a[sortConfig.key]?.toString().toLowerCase() ?? '';
        const valB = b[sortConfig.key]?.toString().toLowerCase() ?? '';
        return sortConfig.direction === 'asc'
          ? valA < valB
            ? -1
            : 1
          : valA > valB
            ? -1
            : 1;
      });
    } else {
      // Default: sort by updatedOn descending (latest first)
      // If updatedOn is not available, fallback to createdAt
      sorted = sorted.sort((a, b) => {
        const dateA = a.updatedOn || a.createdAt || '';
        const dateB = b.updatedOn || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });
    }

    return sorted;
  }, [filteredAutomations, sortConfig]);

  const totalCount = sortedAutomations.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Paginates the sorted automations
  const paginatedAutomations = sortedAutomations.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Calculate which items to show
  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalCount);

  // Updates the sort configuration when a column header is clicked
  const requestSort = (key: keyof Automation) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc')
      direction = 'desc';
    setSortConfig({ key, direction });
  };

  // Returns the appropriate sort icon
  const getSortIcon = (key: keyof Automation) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? (
        <FiChevronUpComponent size={14} />
      ) : (
        <FiChevronDownComponent size={14} />
      );
    }
    return <FiChevronDownComponent size={14} />;
  };

  // Toggles selection of all automations on the current page
  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(paginatedAutomations.map((a) => a.id));
    } else {
      setSelected([]);
    }
  };

  // Toggles selection of a single automation
  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  // Handle page change (0-indexed)
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle opening the portal popup
  const handleMoreClick = (e: React.MouseEvent<HTMLElement>, automation: Automation) => {
    e.stopPropagation();

    if (popupOpen && selectedAutomation?.id === automation.id) {
      handleClosePopup();
      return;
    }

    setSelectedAutomation(automation);
    setAnchorEl(e.currentTarget);
    setPopupOpen(true);
  };

  // Handle closing the portal popup
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedAutomation(null);
    setAnchorEl(null);
  };

  // Handle archive functionality
  const handleArchive = async () => {
    if (!selectedAutomation) return;

    setIsArchiving(true);

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      const idempotencyKey = uuidv4();

      const response = await fetch(
        `${BASE_URL}/api/v1/automations/${selectedAutomation.id}/archive?reason=manual`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Idempotency-Key': idempotencyKey,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to archive automation: ${response.status}`);
      }

      toast.success(`Automation "${selectedAutomation.name}" archived successfully!`, {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);

      handleClosePopup();

    } catch (err: any) {
      // console.error('Failed to archive automation:', err);
      toast.error(err.message || 'Failed to archive automation. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
    } finally {
      setIsArchiving(false);
    }
  };

  // Handle clone functionality
  const handleClone = async () => {
    if (!selectedAutomation) return;

    setIsCloning(true);

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      const idempotencyKey = uuidv4();

      const cloneBody = {
        nameOverride: `${selectedAutomation.name} (Copy)`,
        descriptionOverride: `Cloned from ${selectedAutomation.name}`,
        maskSecrets: true,
        regenerateIds: false
      };

      const response = await fetch(
        `${BASE_URL}/api/v1/automations/${selectedAutomation.id}/clone`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Idempotency-Key': idempotencyKey,
          },
          body: JSON.stringify(cloneBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to clone automation: ${response.status}`);
      }

      const clonedAutomation = await response.json();

      toast.success(`Automation "${selectedAutomation.name}" cloned successfully!`, {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);

      handleClosePopup();

      if (clonedAutomation && clonedAutomation.id) {
        navigate(`/automations/view/${clonedAutomation.id}`);
      }

    } catch (err: any) {
      // console.error('Failed to clone automation:', err);
      toast.error(err.message || 'Failed to clone automation. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
    } finally {
      setIsCloning(false);
    }
  };

  // Exports selected automations as CSV
  const exportCSV = () => {
    const rows = automations.filter((a) => selected.includes(a.id));
    const exportableFields: (keyof Automation)[] = [
      'id',
      'name',
      'trigger',
      'status',
      'createdAt',
      'createdBy',
      'updatedOn',
    ];
    const header = exportableFields.join(',');
    const body = rows
      .map((r) => exportableFields.map((field) => r[field]).join(','))
      .join('\n');
    const csvContent = [header, body].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'automations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setExportOpen(false);
  };

  // Exports selected automations as PDF
  const exportPDF = () => {
    const rows = automations.filter((a) => selected.includes(a.id));
    const exportableFields: (keyof Automation)[] = [
      'id',
      'name',
      'trigger',
      'status',
      'createdAt',
      'createdBy',
      'updatedOn',
    ];
    const doc = new jsPDF();
    autoTable(doc, {
      head: [exportableFields],
      body: rows.map((r) => exportableFields.map((field) => r[field])),
    });
    doc.save('automations.pdf');
    setExportOpen(false);
  };

  return (
    <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm'>
      {/* Portal Popup */}
      <PortalPopup
        isOpen={popupOpen}
        onClose={handleClosePopup}
        onArchive={handleArchive}
        onClone={handleClone}
        automation={selectedAutomation!}
        anchorEl={anchorEl}
      />

      <div className='flex justify-between items-center px-6 pt-4'>
        <div className='flex space-x-6'>
          {['All', 'Active', 'Draft', 'Paused'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab as typeof activeTab);
                setCurrentPage(0);
              }}
              className={`pb-2 font-medium flex items-center pl-2 ${activeTab === tab
                ? 'border-b-2 border-teal-500 text-teal-600'
                : 'text-gray-500'
                }`}
              style={{ fontSize: '14px', fontWeight: 'normal' }}
            >
              {tab}
              <span
                className='flex items-center justify-center mr-2 ml-1.5'
                style={{
                  width: '30px',
                  height: '21px',
                  backgroundColor: activeTab === tab ? '#E0F8FB' : '#F5F8FA',
                  borderRadius: '40px',
                }}
              >
                {tab === 'All'
                  ? automations.length
                  : automations.filter((a) => {
                    if (tab === 'Active') return a.status?.toLowerCase() === 'active' || a.status?.toLowerCase() === 'published';
                    if (tab === 'Draft') return a.status?.toLowerCase() === 'draft';
                    if (tab === 'Paused') return a.status?.toLowerCase() === 'paused';
                    return true;
                  }).length}
              </span>
            </button>
          ))}
        </div>
        <div className='flex items-center gap-2'>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className={`p-2 text-gray-500 hover:text-teal-600 transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              title="Refresh data"
            >
              <FiRefreshCwComponent size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
          )}
          <div className='relative'>
            {/* <button
              onClick={() => setExportOpen((prev) => !prev)}
              disabled={selected.length === 0}
              className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center text-white ${
                selected.length === 0
                  ? 'bg-gray-200 cursor-not-allowed'
                  : 'bg-orange-500'
              }`}
              style={{ fontSize: '14px' }}
            >
              Export{' '}
              <span className='ml-2'>
                <FiChevronDownComponent size={19} />
              </span>
            </button> */}
            {exportOpen && selected.length > 0 && (
              <div className='absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10'>
                <button
                  onClick={exportPDF}
                  className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
                >
                  Export as PDF
                </button>
                <button
                  onClick={exportCSV}
                  className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
                >
                  Export as CSV
                </button>
              </div>
            )}          </div>
        </div>
      </div>

      <div className='px-6'>
        <table className='w-full text-left mt-4 border border-gray-200'>
          <thead className='bg-gray-100'>
            <tr
              className='text-gray-600 text-sm border-b'
              style={{ fontSize: '12px', color: '#657584' }}
            >
              {/* <th className='px-6 py-3'>
                <div className='flex items-center justify-center'>
                  <input
                    type='checkbox'
                    onChange={toggleSelectAll}
                    checked={
                      selected.length === paginatedAutomations.length &&
                      paginatedAutomations.length > 0
                    }
                    className='w-4 h-4 rounded'
                  />
                </div>
              </th> */}
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('name')}
              >
                <div className='flex items-center gap-1'>
                  NAME {getSortIcon('name')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('trigger')}
              >
                <div className='flex items-center gap-1'>
                  TRIGGER TYPE {getSortIcon('trigger')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('status')}
              >
                <div className='flex items-center gap-1'>
                  STATUS {getSortIcon('status')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('createdAt')}
              >
                <div className='flex items-center gap-1'>
                  CREATED AT {getSortIcon('createdAt')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('createdBy')}
              >
                <div className='flex items-center gap-1'>
                  CREATED BY {getSortIcon('createdBy')}
                </div>
              </th>
              <th
                className='px-6 py-3 cursor-pointer font-normal'
                onClick={() => requestSort('updatedOn')}
              >
                <div className='flex items-center gap-1'>
                  UPDATED ON {getSortIcon('updatedOn')}
                </div>
              </th>
              <th className='px-6 py-3 font-normal flex justify-end'>
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
            {paginatedAutomations.map((a) => (
              <tr key={a.id} className='border-b hover:bg-gray-50'>
                {/* <td className='px-6 py-4 flex items-center justify-center'>
                  <input
                    type='checkbox'
                    checked={selected.includes(a.id)}
                    onChange={() => toggleSelect(a.id)}
                    className='w-4 h-4 rounded'
                  />
                </td> */}
                <td className='px-6 py-4'>{a.name || 'Automation Title'}</td>
                <td className='px-6 py-4'>
                  <span
                    className='px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600'
                  >
                    {a.trigger || '-'}
                  </span>
                </td>
                <td className='px-6 py-4'>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      a.status
                    )}`}
                  >
                    {a.status}
                  </span>
                </td>
                <td className='px-6 py-4'>{a.createdAt || '-'}</td>
                <td className='px-6 py-4'>{a.createdBy || '-'}</td>
                <td className='px-6 py-4'>{a.updatedOn || '-'}</td>
                <td className='px-6 py-4 flex items-center justify-end space-x-3'>
                  <button
                    className='text-teal-600 flex items-center gap-1'
                    onClick={() =>
                      navigate(
                        a.status?.toLowerCase() === 'draft'
                          ? `/automations/edit/${a.id}`
                          : `/automations/view/${a.id}`
                      )
                    }
                  >
                    {a.status?.toLowerCase() === 'draft' ? (
                      <FiEditComponent />
                    ) : (
                      <FiEyeComponent />
                    )}
                    <span className='ml-1'>
                      {a.status?.toLowerCase() === 'draft' ? 'Edit' : 'View'}
                    </span>
                  </button>
                  <button
                    onClick={(e) => handleMoreClick(e, a)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiMoreVerticalComponent className='cursor-pointer' />
                  </button>
                </td>
              </tr>
            ))}
            {paginatedAutomations.length === 0 && (
              <tr>
                <td colSpan={7} className='px-6 py-6 text-center text-gray-500'>
                  No automations found for "{activeTab}"
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODERN PAGINATION CONTROLS */}
      <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500'>
        <span>
          Showing {startItem} to {endItem} of {totalCount} automations
        </span>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {(() => {
              const pages = [];
              const total = totalPages;
              const current = currentPage + 1;
              const delta = 1;

              pages.push(1);

              let rangeStart = Math.max(2, current - delta);
              let rangeEnd = Math.min(total - 1, current + delta);

              if (rangeStart > 2) {
                pages.push('...');
              }

              for (let i = rangeStart; i <= rangeEnd; i++) {
                pages.push(i);
              }

              if (rangeEnd < total - 1) {
                pages.push('...');
              }

              if (total > 1) {
                pages.push(total);
              }

              return pages.map((page, index) => {
                if (page === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-2 text-sm text-gray-500"
                    >
                      …
                    </span>
                  );
                }

                const pageNumber = page as number;
                const isActive = pageNumber === current;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber - 1)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {pageNumber}
                  </button>
                );
              });
            })()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage + 1 === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationsTable;